// Third party
import React, { Component } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

// Actions
import { setSelectedFunds, removeSelectedFunds, addSelectedFunds } from '@/redux/modules/advanced-search';

// Components
import FundDetails from './FundDetails';
import { withErrorBoundary } from '../errors';
import AdvancedSearchResult from './AdvancedSearchResult';

// Enums
import { SearchType } from '@/enums/AdvancedSearch';

// Table Columns Definition
import { getColumnsDefinition, SearchResultColumns } from './columns-definition/SearchResult';

// Utils
import { FormattedMessage } from '@/utils/i18n';

// Types
import { FundSearchState } from '@/redux/modules/types/fund-search';
import { MultiselectAction, ColumnOverride } from '@/types/data-table';
import { ReduxState } from '@/redux/modules/types';
import AdvancedSearchItem from '@/types/advanced-search-item';
import Fund, { FundForDataTable } from '@/types/fund';
import User from '@/types/user';
import Locale from '@/types/locale';

// Legacy support
import LegacyFundSearchResult from './LegacyFundSearchResult';
import useThemeVersion from '@/hooks/useThemeVersion';

type DispatchProps = {
  setSelectedFunds: (funds: Fund[]) => void;
  removeSelectedFunds: (funds: Fund[]) => void;
  addSelectedFunds: (funds: Fund[]) => void;
};

type Props = DispatchProps & {
  addingToComparison: boolean;
  addingToPortfolio: boolean;
  canSelect?: boolean;
  className?: string;
  classicVersionOnly?: boolean;
  compact?: boolean;
  customTableActions?: MultiselectAction<AdvancedSearchItem>[] | JSX.Element[];
  focusOnRowClick: boolean;
  fundState: FundSearchState;
  hasMore: boolean;
  hideCompareAllAlternatives?: boolean;
  hideSaveFund?: boolean;
  insideModal?: boolean;
  loading?: boolean;
  onAddItem: (fund: Fund) => void;
  onAddToPortfolio: (funds: Fund[]) => void;
  onClearComplianceAlternatives: () => void;
  onCompareAlternatives?: () => void;
  onLoadMoreResults: () => void;
  onStartComparison: (funds: Fund[]) => void;
  onToggleSaved: (fund: Fund) => void;
  savedFunds: Fund[];
  setFocusedFund: (fund?: Fund) => void;
  showInlineAdd?: boolean;
  totalResultsCount?: number;
  useWindowScrollAncestor?: boolean;
  searchQuery?: string;
  user?: User;
  locale?: Locale;
  showTicker?: boolean;
  tableColumns: SearchResultColumns[];
  columnPinning?: { left?: string[]; right?: string[] };
  columnOverrides?: Partial<Record<SearchResultColumns, ColumnOverride>>;
};

type LegacyProps = {
  overrideShowArrow?: boolean;
};

class FundSearchResultV2 extends Component<Props> {
  static defaultProps = {
    onToggleSaved: () => {},
    onAddItem: () => {},
    focusOnRowClick: true,
  };

  lastFocusedElement: HTMLElement | null = null;

  state = {
    selectedRows: [],
  };

  componentDidUpdate(prevProps: Readonly<Props>) {
    // Reset the fund details when a new search is triggered
    if (this.props.loading !== prevProps.loading) {
      this.setFocusedFund(undefined);
    }
  }

  getActions = () => {
    const { addingToPortfolio, addingToComparison, onAddToPortfolio, onStartComparison } = this.props;
    return [
      {
        loading: addingToPortfolio,
        icon: 'plus',
        label: <FormattedMessage id={`Search.Funds.${addingToPortfolio ? 'AddingToPortfolio' : 'AddToPortfolio'}`} />,
        onClick: onAddToPortfolio,
      },
      {
        isLoading: addingToComparison,
        icon: 'plus',
        label: <FormattedMessage id={`Search.Funds.${addingToComparison ? 'AddingToComparison' : 'AddToComparison'}`} />,
        onClick: onStartComparison,
      },
    ];
  };

  hideDetails = () => {
    this.setFocusedFund(undefined);
    const lastFocused = this.lastFocusedElement;
    if (lastFocused && document.body.contains(lastFocused)) {
      lastFocused.focus();
    }
    this.lastFocusedElement = null;
  };

  onRowClick = (fund: Fund) => {
    this.lastFocusedElement = document.activeElement as HTMLElement;
    this.setFocusedFund(fund);
  };

  onCompareAlternatives = (id: string) => {
    const { onCompareAlternatives } = this.props;

    if (onCompareAlternatives) {
      onCompareAlternatives();
    }
    const fund = this.props.fundState?.funds?.find(f => f._id === id) ?? null;
    if (fund) {
      this.onRowClick(fund);
    }
  };

  setFocusedFund = (item?: Fund) => {
    this.props.setFocusedFund(item);

    if (item?._id !== this.props.fundState.focusedFund?._id) {
      this.props.onClearComplianceAlternatives();
    }
  };

  getFundDetails = () => {
    const {
      fundState: { focusedFund },
      hideCompareAllAlternatives,
      onAddItem,
    } = this.props;

    return (
      focusedFund && (
        <FundDetails
          fund={focusedFund}
          hideCompareAll={hideCompareAllAlternatives}
          onAddItem={onAddItem}
          onClose={this.hideDetails}
          onCompareAlternatives={this.onCompareAlternatives}
        />
      )
    );
  };

  updateStoreWithSelectedRows = (upcomingSelectedRows: Fund[], prevSelectedRows: Fund[]) => {
    if (upcomingSelectedRows !== prevSelectedRows) {
      const upcomingIds = upcomingSelectedRows.map(row => row._id);
      const previousIds = prevSelectedRows.map(row => row._id);

      this.removeSelectedRowsFromStore(prevSelectedRows, upcomingIds);
      this.addSelectedRowsToStore(upcomingSelectedRows, previousIds);
    }
  };

  removeSelectedRowsFromStore = (prevSelectedRows: Fund[], upcomingIds: string[]) => {
    const rowsToRemove = prevSelectedRows.filter(row => !upcomingIds.includes(row._id));
    if (rowsToRemove.length) {
      this.props.removeSelectedFunds(rowsToRemove);
    }
  };

  addSelectedRowsToStore = (upcomingSelectedRows: Fund[], previousIds: string[]) => {
    const rowsToAdd = upcomingSelectedRows.filter(row => !previousIds.includes(row._id));
    if (rowsToAdd.length) {
      this.props.addSelectedFunds(rowsToAdd);
    }
  };

  handleRowSelectionChange = (upcomingSelectedRows: AdvancedSearchItem[]) => {
    this.updateStoreWithSelectedRows(upcomingSelectedRows as Fund[], this.state.selectedRows);
    this.setState({ selectedRows: upcomingSelectedRows });
  };

  clearFundsSelection = () => {
    this.setState({ selectedRows: [] });
    this.props.setSelectedFunds([]);
  };

  render() {
    const {
      fundState: { selectedFunds },
      fundState: { focusedFund, funds },
      insideModal,
      loading,
      totalResultsCount,
      useWindowScrollAncestor,
      hasMore,
      compact,
      canSelect,
      customTableActions,
      focusOnRowClick,
      onAddItem,
      onToggleSaved,
      savedFunds,
      fundState,
      showInlineAdd,
      hideSaveFund,
      user,
      locale,
      showTicker,
      columnPinning,
      tableColumns,
      columnOverrides,
    } = this.props;

    const { columns, columnVisibilityState } = getColumnsDefinition<FundForDataTable>({
      onAddItem,
      onToggleSaved,
      itemState: fundState,
      showInlineAdd,
      showTicker,
      hideSaveItem: hideSaveFund,
      allowedColumns: tableColumns,
      type: SearchType.FUNDS,
      user,
      locale,
      columnOverrides,
    });

    return (
      <AdvancedSearchResult
        actions={this.getActions()}
        canSelect={canSelect}
        classicVersionOnly={this.props.classicVersionOnly}
        className={classNames('fund-search-result', this.props.className)}
        columnVisibilityState={columnVisibilityState}
        columnPinning={columnPinning}
        columns={columns}
        compact={compact}
        customTableActions={customTableActions}
        data={funds}
        focusedItem={focusedFund}
        hasMore={hasMore}
        insideModal={insideModal}
        itemDetails={this.getFundDetails()}
        itemType={SearchType.FUNDS}
        loading={loading}
        onLoadMoreResults={this.props.onLoadMoreResults}
        onClearSelection={this.clearFundsSelection}
        onRowClick={focusOnRowClick ? this.onRowClick : undefined}
        onRowSelectionChange={this.handleRowSelectionChange}
        savedItems={savedFunds}
        searchQuery={this.props.searchQuery}
        selectedItems={selectedFunds}
        selectedTitle="Search.Funds.SelectedTitle"
        totalResultsCount={totalResultsCount}
        useWindowScrollAncestor={useWindowScrollAncestor}
      />
    );
  }
}

const FundSearchResult = (props: Props & LegacyProps) => {
  const { isLightVersion } = useThemeVersion();
  const { classicVersionOnly } = props;
  return isLightVersion && !classicVersionOnly ? <FundSearchResultV2 {...props} /> : <LegacyFundSearchResult {...props} />;
};

const mapStateToProps = (state: ReduxState) => ({
  user: state.auth.user,
  locale: state.intl.locale,
});

export default connect(mapStateToProps, {
  setSelectedFunds,
  removeSelectedFunds,
  addSelectedFunds,
})(withErrorBoundary(FundSearchResult));
